import { DateTime } from "luxon";

import type { IDateRange } from "@/data/model/Dashboard";
import { DashboardStatsGranularity } from "@/data/model/Enums";
import { DateTime as DateTimeHelper } from "@/data/util/DateTime";

import { i18n } from "@/portal/Trans";

export type ChartLabelMapping = {
    dateRange: IDateRange;
    scaleLabel: string;
    tooltipLabel: string;
};

export function getChartLabels(dateRanges: IDateRange[], granularity: DashboardStatsGranularity) {
    const results: ChartLabelMapping[] = [];
    const chartLabelStrings = getChartLabelsForGranularity(dateRanges, granularity);
    const tooltipLabelStrings = getTooltipLabelsForGranularity(dateRanges, granularity);

    dateRanges.forEach((dateRange, index) => {
        results.push({
            dateRange: dateRange,
            scaleLabel: chartLabelStrings[index],
            tooltipLabel: tooltipLabelStrings[index],
        });
    });

    return results;
}

export function getChartLabelsForGranularity(
    dateRanges: IDateRange[],
    granularity: DashboardStatsGranularity
): string[] {
    if (granularity == DashboardStatsGranularity.Hours) {
        return dateRanges.map(
            (dateRange) =>
                `${DateTimeHelper.toDayOfMonthString(dateRange.from)} ${DateTimeHelper.toTimeString(dateRange.from)}`
        );
    }

    if (granularity == DashboardStatsGranularity.Days) {
        const days = getShortWeekDays();
        return dateRanges.map(
            (dateRange) => `${days[dateRange.from.getDay()]} ${DateTimeHelper.toDayOfMonthString(dateRange.from)}`
        );
    }

    if (granularity == DashboardStatsGranularity.Weeks) {
        return dateRanges.map(
            (dateRange) => {
                const to = dateRange.to;
                to.setDate(to.getDate() -1 );
                return `${DateTimeHelper.toDayOfMonthString(dateRange.from)} - ${DateTimeHelper.toDayOfMonthString(to)}`
            }
        );
    }

    if (granularity == DashboardStatsGranularity.Months) {
        return getMonthLabels(dateRanges);
    }

    return [];
}

export function getTooltipLabelsForGranularity(
    dateRanges: IDateRange[],
    granularity: DashboardStatsGranularity
): string[] {
    if (granularity == DashboardStatsGranularity.Hours) {
        return dateRanges.map((dateRange) => {
            const lastDateTo = DateTime.fromJSDate(dateRange.to).minus({ millisecond: 1 }).toJSDate();
            return `${DateTimeHelper.toDayOfMonthString(dateRange.from)} ${DateTimeHelper.toTimeString(dateRange.from)}-${DateTimeHelper.toTimeString(lastDateTo)}`;
        });
    }

    if (granularity == DashboardStatsGranularity.Days) {
        const days = getWeekDays();
        return dateRanges.map((dateRange) => {
            return `${days[dateRange.from.getDay()]} ${DateTimeHelper.toDayOfMonthString(dateRange.from)}`;
        });
    }

    if (granularity == DashboardStatsGranularity.Weeks) {
        return dateRanges.map((dateRange) => {
            return `${DateTimeHelper.toDayOfMonthString(dateRange.from)} - ${DateTimeHelper.toDayOfMonthString(dateRange.to)}`;
        });
    }

    if (granularity == DashboardStatsGranularity.Months) {
        return getMonthLabels(dateRanges);
    }

    return [];
}

function getMonthLabels(dateRanges: IDateRange[]): string[] {
    const monthNames = getMonths();
    return dateRanges.map((dateRange) => {
        // Take the label from the middle of the range: the location's business day start time
        // offsets both bounds by a few hours, which can push from/to into an adjacent month
        const middle = new Date((dateRange.from.getTime() + dateRange.to.getTime()) / 2);
        return `${monthNames[middle.getUTCMonth()]} ${middle.getUTCFullYear()}`;
    });
}

export function getMonths() {
    return [
        i18n("enums.Month.January"),
        i18n("enums.Month.February"),
        i18n("enums.Month.March"),
        i18n("enums.Month.April"),
        i18n("enums.Month.May"),
        i18n("enums.Month.June"),
        i18n("enums.Month.July"),
        i18n("enums.Month.August"),
        i18n("enums.Month.September"),
        i18n("enums.Month.October"),
        i18n("enums.Month.November"),
        i18n("enums.Month.December"),
    ];
}

export function getWeekDays() {
    return [
        i18n("enums.Weekday.Sunday"),
        i18n("enums.Weekday.Monday"),
        i18n("enums.Weekday.Tuesday"),
        i18n("enums.Weekday.Wednesday"),
        i18n("enums.Weekday.Thursday"),
        i18n("enums.Weekday.Friday"),
        i18n("enums.Weekday.Saturday"),
    ];
}

export function getShortWeekDays() {
    return [
        i18n("enums.Weekday3Letters.Sunday"),
        i18n("enums.Weekday3Letters.Monday"),
        i18n("enums.Weekday3Letters.Tuesday"),
        i18n("enums.Weekday3Letters.Wednesday"),
        i18n("enums.Weekday3Letters.Thursday"),
        i18n("enums.Weekday3Letters.Friday"),
        i18n("enums.Weekday3Letters.Saturday"),
    ];
}
